import { useEffect, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { createFacilityAsset, listFacilityAssets, listFacilityGroups } from "../lib/api";
import { useApi, useMutation } from "../lib/useApi";
import { PageBar } from "../components/Layout";
import { Modal } from "../components/Modal";
import { Alert, EmptyState, ErrorState, Loading, Spinner } from "../components/States";
import { SortTh, useTableSort, type SortAccessor } from "../components/SortableTable";
import { FACILITY_ASSET_STATUSES, FACILITY_CATEGORIES } from "../lib/constants";
import type { CreateFacilityAssetInput, FacilityAssetRow } from "../lib/types";
import { RichTextEditor, richTextIsEmpty } from "../components/RichText";
import { facilityLabel } from "./FacilitiesPage";

const FACILITY_ASSET_SORT: Record<string, SortAccessor<FacilityAssetRow>> = {
  tag: (a) => a.asset_tag,
  name: (a) => a.name,
  category: (a) => facilityLabel(FACILITY_CATEGORIES, a.category),
  location: (a) => [a.building, a.location].filter(Boolean).join(" · "),
  status: (a) => facilityLabel(FACILITY_ASSET_STATUSES, a.status),
};

/** Status pill for a facility equipment row. */
export function FacilityAssetStatusBadge({ status }: { status: string }) {
  return (
    <span className={`badge facility-asset-st facility-asset-st-${status}`}>
      {facilityLabel(FACILITY_ASSET_STATUSES, status)}
    </span>
  );
}

/** Equipment registry — searchable list of facility assets with a status filter. */
export function FacilityAssetsPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [status, setStatus] = useState("");
  // Group is a client-side convenience filter ("" = all, "none" = unassigned).
  const [group, setGroup] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, loading, error, reload } = useApi(
    (signal) =>
      listFacilityAssets(
        { ...(debounced ? { q: debounced } : {}), ...(status ? { status } : {}) },
        signal,
      ),
    [debounced, status],
  );
  const allAssets = data?.assets ?? [];
  const assets =
    group === "none"
      ? allAssets.filter((a) => !a.group_id)
      : group
        ? allAssets.filter((a) => a.group_id === group)
        : allAssets;
  const { rows: sortedAssets, sort, toggle } = useTableSort(assets, FACILITY_ASSET_SORT);
  const filtersActive = !!(debounced || status || group);

  return (
    <>
      <PageBar
        crumbs="Equipment"
        actions={
          <button className="btn primary" onClick={() => setShowCreate(true)}>
            + New equipment
          </button>
        }
      />
      <div className="content">
        <div className="card">
          <div className="card-head">
            <h3>Equipment</h3>
            <div className="btn-row" style={{ alignItems: "center" }}>
              <input
                type="text"
                className="input-inline"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search name or tag…"
                aria-label="Search equipment"
              />
              <select
                className="input-inline"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                {FACILITY_ASSET_STATUSES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.label}
                  </option>
                ))}
              </select>
              {groups.length > 0 ? (
                <select
                  className="input-inline"
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  aria-label="Filter by group"
                >
                  <option value="">All groups</option>
                  <option value="none">Unassigned</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </select>
              ) : null}
              <span className="faint">{assets.length}</span>
            </div>
          </div>
          <div className="card-body">
            {loading ? (
              <Loading label="Loading equipment…" />
            ) : error ? (
              <ErrorState error={error} onRetry={reload} />
            ) : assets.length === 0 ? (
              <EmptyState>
                {filtersActive
                  ? "No equipment matches the current filters."
                  : "No equipment yet. Add the first asset to start tracking it."}
              </EmptyState>
            ) : (
              <table className="data">
                <thead>
                  <tr>
                    <SortTh field="tag" sort={sort} onSort={toggle}>Tag</SortTh>
                    <SortTh field="name" sort={sort} onSort={toggle}>Name</SortTh>
                    <SortTh field="category" sort={sort} onSort={toggle}>Category</SortTh>
                    <SortTh field="location" sort={sort} onSort={toggle}>Location</SortTh>
                    <SortTh field="status" sort={sort} onSort={toggle}>Status</SortTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedAssets.map((a) => (
                    <FacilityAssetTr
                      key={a.id}
                      asset={a}
                      onOpen={() => navigate(`/facilities/assets/${a.id}`)}
                    />
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>

      {showCreate ? (
        <FacilityAssetModal
          asset={null}
          onClose={() => setShowCreate(false)}
          onSaved={(createdId) => {
            setShowCreate(false);
            reload();
            if (createdId) navigate(`/facilities/assets/${createdId}`);
          }}
        />
      ) : null}
    </>
  );
}

function FacilityAssetTr({ asset, onOpen }: { asset: FacilityAssetRow; onOpen: () => void }) {
  const locationText = [asset.building, asset.location].filter(Boolean).join(" · ") || "—";
  return (
    <tr
      className="clickable"
      onClick={onOpen}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpen();
      }}
    >
      <td className="muted">{asset.asset_tag || "—"}</td>
      <td>
        <strong>{asset.name}</strong>
      </td>
      <td className="muted">{facilityLabel(FACILITY_CATEGORIES, asset.category)}</td>
      <td className="muted">{locationText}</td>
      <td>
        <FacilityAssetStatusBadge status={asset.status} />
      </td>
    </tr>
  );
}

/** Add/edit equipment modal — null asset = create. The detail page reuses this
 *  for edits, passing the existing asset + an update handler. */
export function FacilityAssetModal({
  asset,
  onClose,
  onSaved,
  onUpdate,
}: {
  asset: FacilityAssetRow | null;
  onClose: () => void;
  onSaved: (createdId?: string) => void;
  /** Provided when editing — called with the patch instead of create. */
  onUpdate?: (patch: CreateFacilityAssetInput) => Promise<boolean>;
}) {
  const [name, setName] = useState(asset?.name ?? "");
  const [tag, setTag] = useState(asset?.asset_tag ?? "");
  const [category, setCategory] = useState(asset?.category ?? "");
  const [building, setBuilding] = useState(asset?.building ?? "");
  const [location, setLocation] = useState(asset?.location ?? "");
  const [status, setStatus] = useState(asset?.status ?? "operational");
  const [manufacturer, setManufacturer] = useState(asset?.manufacturer ?? "");
  const [model, setModel] = useState(asset?.model ?? "");
  const [serial, setSerial] = useState(asset?.serial ?? "");
  const [notes, setNotes] = useState(asset?.notes ?? "");
  const [groupId, setGroupId] = useState(asset?.group_id ?? "");

  const groupsApi = useApi((signal) => listFacilityGroups(signal), []);
  const groups = groupsApi.data?.groups ?? [];

  const createMut = useMutation(createFacilityAsset);
  const [updatePending, setUpdatePending] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const pending = createMut.pending || updatePending;
  const error = createMut.error || updateError;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    const common: CreateFacilityAssetInput = {
      name: name.trim(),
      asset_tag: tag.trim() || null,
      category: category || null,
      building: building.trim() || null,
      location: location.trim() || null,
      status,
      manufacturer: manufacturer.trim() || null,
      model: model.trim() || null,
      serial: serial.trim() || null,
      notes: richTextIsEmpty(notes) ? null : notes,
      group_id: groupId || null,
    };
    if (asset && onUpdate) {
      setUpdateError(null);
      setUpdatePending(true);
      try {
        const ok = await onUpdate(common);
        if (ok) onSaved();
      } catch (err) {
        setUpdateError(err instanceof Error ? err.message : "Save failed.");
      } finally {
        setUpdatePending(false);
      }
    } else {
      const res = await createMut.run(common);
      if (res) onSaved(res.asset.id);
    }
  }

  return (
    <Modal
      title={asset ? "Edit equipment" : "Add equipment"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={pending}>
            Cancel
          </button>
          <button
            className="btn primary"
            type="submit"
            form="facility-asset-form"
            disabled={pending || !name.trim()}
          >
            {pending ? <Spinner label="Saving…" /> : asset ? "Save changes" : "Add equipment"}
          </button>
        </>
      }
    >
      {error ? <Alert kind="error">{error}</Alert> : null}
      <form id="facility-asset-form" onSubmit={onSubmit}>
        <label className="field">
          <span className="label">Name</span>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Rooftop AHU-3"
            autoFocus
            required
          />
        </label>
        <div className="row">
          <label className="field">
            <span className="label">Asset tag</span>
            <input
              type="text"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              placeholder="FAC-0142"
            />
          </label>
          <label className="field">
            <span className="label">Category</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="">—</option>
              {FACILITY_CATEGORIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Building</span>
            <input
              type="text"
              value={building}
              onChange={(e) => setBuilding(e.target.value)}
              placeholder="e.g. Science Hall"
            />
          </label>
          <label className="field">
            <span className="label">Location</span>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. Mechanical room"
            />
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {FACILITY_ASSET_STATUSES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Manufacturer</span>
            <input
              type="text"
              value={manufacturer}
              onChange={(e) => setManufacturer(e.target.value)}
              placeholder="e.g. Trane"
            />
          </label>
        </div>
        <div className="row">
          <label className="field">
            <span className="label">Model</span>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Serial</span>
            <input type="text" value={serial} onChange={(e) => setSerial(e.target.value)} />
          </label>
        </div>
        {groups.length > 0 || asset?.group_id ? (
          <label className="field">
            <span className="label">Group <span className="faint">(controls who can see this equipment)</span></span>
            <select value={groupId} onChange={(e) => setGroupId(e.target.value)} disabled={groupsApi.loading}>
              <option value="">Unassigned (everyone with Facilities access)</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
              {groupId && !groups.some((g) => g.id === groupId) ? (
                <option value={groupId}>{groupId}</option>
              ) : null}
            </select>
          </label>
        ) : null}
        <label className="field">
          <span className="label">Notes</span>
          <RichTextEditor value={notes} onChange={setNotes} placeholder="Notes…" />
        </label>
      </form>
    </Modal>
  );
}
